import useQueryParameters from 'hooks/useQueryParameters';
import { useUnifiedIsInSession } from 'hooks/useWorkflow/unifiedPrequalCheckoutWorkflow';
import { map, prop } from 'ramda';
import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { InstallmentsOffer, SplitPayOffer } from 'state/financeOffers';
import { selectInstantCredit } from 'state/instantCredit';
import { PrequalOffer } from 'state/prequalOffer';
import { isOfferConfigured, selectTenant } from 'state/tenant';
import isPresent from 'utils/isPresent';

type WidgetsHook = (
  props: Props,
) => () => {
  hasSplitpayWidget: boolean;
  hasInstallmentsWidget: boolean;
  hasPrequalWidget: boolean;
  hasDivider: boolean;
  hasUnhappyPathDivider: boolean;
  hasExistingAccountWidget: boolean;
  hasNotQualifiedWidget: boolean;
  hasPrequalFallbackWidget: boolean;
  hasFallbackMessageWidget: boolean;
};

interface Props {
  splitPayOffers: SplitPayOffer[];
  installmentOffers: InstallmentsOffer[];
  principalDebounced: number;
  prequalOffer: PrequalOffer;
}

const useWidgets: WidgetsHook = (props: Props) => {
  const { bread } = useSelector(selectTenant);
  const inSession = useUnifiedIsInSession();
  const {
    splitPayOffers,
    installmentOffers,
    principalDebounced,
    prequalOffer,
  } = props;

  const instantCredit = useSelector(selectInstantCredit);

  const {
    hasExistingAccount: queryParamHasExistingAccount,
  } = useQueryParameters();

  // First consider icHasExistingAccount, if undefined consider
  const hasExistingAccount =
    instantCredit.hasExistingAccount ||
    prequalOffer.hasExistingAccount ||
    queryParamHasExistingAccount;

  const splitpayCapacityExceeded = useMemo(() => {
    return (
      splitPayOffers[0] &&
      principalDebounced > splitPayOffers[0].capacityValue / 100
    );
  }, [principalDebounced, splitPayOffers]);
  const installmentMaxCapacity = useMemo(() => {
    const result = Math.max(...map(prop('capacityValue'), installmentOffers));
    return result ? result / 100 : undefined;
  }, [installmentOffers]);
  const installmentCapacityExceeded = useMemo(() => {
    return Boolean(
      installmentMaxCapacity && principalDebounced > installmentMaxCapacity,
    );
  }, [principalDebounced, installmentMaxCapacity]);

  const creditLimitExceeded = useMemo(() => {
    return Boolean(
      !prequalOffer.creditLimit ||
        (prequalOffer.creditLimit &&
          principalDebounced > prequalOffer.creditLimit),
    );
  }, [prequalOffer.creditLimit, principalDebounced]);

  const hasSplitpayWidget =
    isOfferConfigured('splitpay', bread) &&
    isPresent(splitPayOffers) &&
    !splitpayCapacityExceeded;

  const hasInstallmentsWidget =
    isOfferConfigured('installments', bread) &&
    isPresent(installmentOffers) &&
    !installmentCapacityExceeded;

  const hasExistingAccountWidget =
    hasExistingAccount && isOfferConfigured('prequalification', bread);

  const hasPrequalWidget =
    isOfferConfigured('prequalification', bread) &&
    Boolean(prequalOffer.prequalificationId) &&
    Boolean(prequalOffer.hasOffer) &&
    !hasExistingAccountWidget &&
    !creditLimitExceeded;

  const hasNotQualifiedWidget =
    !hasPrequalWidget &&
    !hasExistingAccountWidget &&
    isOfferConfigured('prequalification', bread) &&
    Boolean(prequalOffer.notQualified);

  const hasPrequalFallbackWidget =
    isOfferConfigured('prequalification', bread) &&
    inSession &&
    !hasExistingAccountWidget &&
    !hasPrequalWidget &&
    !hasNotQualifiedWidget;

  const hasUnhappyPathDivider = hasInstallmentsWidget || hasSplitpayWidget;

  const hasDivider =
    (hasInstallmentsWidget || hasSplitpayWidget) &&
    isOfferConfigured('prequalification', bread) &&
    hasPrequalWidget;

  const hasFallbackMessageWidget =
    !hasSplitpayWidget &&
    !hasInstallmentsWidget &&
    !hasPrequalWidget &&
    !hasExistingAccountWidget &&
    !hasNotQualifiedWidget &&
    !hasPrequalFallbackWidget;

  return useCallback(
    () => ({
      hasSplitpayWidget,
      hasInstallmentsWidget,
      hasPrequalWidget,
      hasDivider,
      hasUnhappyPathDivider,
      hasExistingAccountWidget,
      hasNotQualifiedWidget,
      hasPrequalFallbackWidget,
      hasFallbackMessageWidget,
    }),
    [
      hasSplitpayWidget,
      hasInstallmentsWidget,
      hasPrequalWidget,
      hasDivider,
      hasUnhappyPathDivider,
      hasExistingAccountWidget,
      hasNotQualifiedWidget,
      hasPrequalFallbackWidget,
      hasFallbackMessageWidget,
    ],
  );
};

export default useWidgets;
